/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import sipLogo from '../../assets/sip_logo_blue.svg';
import { Spin } from 'antd';
import { CometChatUIKit, CometChatUIKitLoginListener } from '@cometchat/chat-uikit-react';
import { CometChat } from '@cometchat/chat-sdk-javascript';
import { toast } from 'sonner'
import { platformService, hexToBuffer, isInExt } from '@meme4labs/sip-platform'
import { shortenString } from 'CometChat/utils/strings'
import { __IS_DEV__ } from 'CometChat/utils/env'
import { buildUiKitSettings, getSipTokenStorageKey } from 'CometChat/utils/config'
import { userAPI } from 'services'
import bs58 from "bs58";

import '../../styles/CometChatLogin/CometChatLogin.css';

type User = {
  name: string;
  uid: string;
  avatar: string;
};

type UserJson = {
  users: User[];
};

const walletLoginMessage = `Login to Sip Chat with your wallet. Your wallet address acts as your unique ID—secure, no extra passwords needed. We won’t access your private key or funds. Authorize to start chatting!`

const contentStyle: React.CSSProperties = {
  padding: 50,
  background: 'rgba(0, 0, 0, 0.05)',
  borderRadius: 4,
};

const content = <div style={contentStyle} />;

const CometChatLogin = () => {
  const [defaultUsers, setDefaultUsers] = useState<User[]>([]);
  const [isError, setIsError] = useState(false);
  const [uid, setUid] = useState('');
  const [nonceData, setNonceData] = useState({
    message: '',
    nonce: ''
  });
  const [isPending, setIsPending] = useState(false);
  const [selectedUid, setSelectedUid] = useState('');
  const $isInExt = isInExt();

  async function init() {
    const res = await userAPI.getNonceData()
    setNonceData(res)
    // 在插件中自动登录
    if ($isInExt) {
      handleWalletLogin()
    }
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      init()
    }, 100)

    return () => {
      clearTimeout(timer)
    }
  }, []);

  function buildNewUser(uid: string) {
    const user = new CometChat.User(
      uid
    )
    const name = shortenString(uid, 6, 4)
    user.setName(name)
    user.setAvatar(`https://api.dicebear.com/9.x/avataaars-neutral/svg?seed=${name}`)
    return user
  }

  function updateDefaultAvatar (user: CometChat.User) {
    const newUser = new CometChat.User(
      {uid: user.getUid()}
    )
    newUser.setAvatar(`https://api.dicebear.com/9.x/avataaars-neutral/svg?seed=${user.getName()}`)
    CometChat.updateCurrentUserDetails(newUser)
  }

  async function createNewUserAndLogin(uid: string) {
    try {
      setIsPending(true);
      const user = buildNewUser(uid)
      await CometChatUIKit.createUser(user);
      await login({
        walletAddress: uid
      }, true);
    } catch (error: any) {
      toast.error(error.message);
      console.error('createNewUserAndLogin failed', error);
    }
    setIsPending(false);
  }

  async function login(loginParams: {
    walletAddress: string,
    signature?: string,
    nonce?: string
  }, autoCreateUser?: boolean) {
    setSelectedUid(loginParams.walletAddress);
    try {
      if (autoCreateUser) {
        setIsPending(true);
      }
      if (loginParams.signature && loginParams.nonce) {
        const authData = await userAPI.authLogin(loginParams as any)
        const uiKitSettings = buildUiKitSettings(authData.authToken);
        const sipTokenKey = getSipTokenStorageKey(uiKitSettings.appId);
        localStorage.setItem(sipTokenKey, authData.token);
        // const user = await CometChatUIKit.loginWithAuthToken(authData.authToken);
        const user = await CometChat.login(authData.authToken);
        if (!user.getAvatar()) {
          updateDefaultAvatar(user)
        }
      } else {
        await CometChatUIKit.login(loginParams.walletAddress);
      }
      toast.success('Login successful :)');
      setIsPending(false);
    } catch (error: any) {
      setIsError(true);
      setIsPending(false);
      toast.error(error.message || 'Please login with wallet in prod.');
      console.error('login failed', error);
    }
  }

  async function handleLoginWithUidFormSubmit() {
    if (!uid) {
      toast.error(`UID can't be empty.`);
      return;
    }
    try {
      await login({
        walletAddress: uid
      });
    } catch (error: any) {
      console.error('handleLoginWithUidFormSubmit failed', error);
    }
  }

  const handleWalletLogin = async () => {
    try {
      // @TODO: 添加一个接口选择钱包第一个账号，而不是当前账户
      const account = await platformService.getAccount()
      const signature = await platformService.signMessage(nonceData.message, account)
      await login({
        walletAddress: account.walletAddress,
        nonce: nonceData.nonce,
        signature: bs58.encode(hexToBuffer(signature) as any)
      }, true)
    } catch (error: any) {
      toast.error(error.message || 'Wallet login failed');
    }
    // toast.error('Wallet login is under development.');
  };

  if ($isInExt && !isError) {
    return (
    <div className="cometchat-login__container">
      <Spin size='default' tip="Authing...">
        {content}
      </Spin>
    </div>
    )
  }

  return (
    <div className="cometchat-login__container">
      <div className="cometchat-login__logo">
        {<img src={sipLogo} alt="" />}
      </div>
      <div className="cometchat-login__content">
        {!$isInExt &&
        <>
        <div className="cometchat-login__header">
          <div className="cometchat-login__title">Sign in to Sip Chat</div>
        </div>

        <div className="cometchat-login__custom-login">
          <div className="cometchat-login__form">
            <div className="cometchat-login__input-group">
              <input
                className="cometchat-login__input"
                type="text"
                value={uid}
                onChange={(e) => {
                  setUid(e.target.value);
                }}
                placeholder="Enter your UID"
              />
            </div>

            <button className="cometchat-login__submit-button" onClick={handleLoginWithUidFormSubmit}>Login</button>
          </div>
        </div>
        </>
        }

        {!$isInExt &&
        <div className="cometchat-login__divider-section" style={{ display: 'flex' }}>
          <div className="cometchat-login__divider" />
          <span className="cometchat-login__divider-text"> Or </span>
          <div className="cometchat-login__divider" />
        </div>
        }

        <button className="cometchat-login__submit-button" onClick={handleWalletLogin}>{ isPending ? 'Connecting...' : 'Connect with Sip Wallet'} </button>

      </div>
    </div>
  );
};

export default CometChatLogin;
